import { findMarketByLocale } from "@features/market/config/market-config";

const DEFAULT_FORMATTER_OPTIONS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "long",
  day: "numeric",
};

const FORMAT_OPTION_KEY_ORDER = [
  "calendar",
  "dateStyle",
  "day",
  "dayPeriod",
  "era",
  "formatMatcher",
  "fractionalSecondDigits",
  "hour",
  "hour12",
  "hourCycle",
  "minute",
  "month",
  "numberingSystem",
  "second",
  "timeStyle",
  "timeZone",
  "timeZoneName",
  "weekday",
  "year",
  "localeMatcher",
] as const satisfies readonly (keyof Intl.DateTimeFormatOptions)[];

const defaultFormatterCache = new Map<string, Intl.DateTimeFormat>();
const formatterCache = new Map<string, Intl.DateTimeFormat>();
const formatterByOptionsIdentityCache = new Map<
  string,
  WeakMap<Intl.DateTimeFormatOptions, Intl.DateTimeFormat>
>();

function getDefaultFormatter(locale: string) {
  const cachedFormatter = defaultFormatterCache.get(locale);
  if (cachedFormatter !== undefined) return cachedFormatter;

  const formatter = new Intl.DateTimeFormat(locale, DEFAULT_FORMATTER_OPTIONS);
  defaultFormatterCache.set(locale, formatter);

  return formatter;
}

function getOptionsKey(options: Intl.DateTimeFormatOptions) {
  let key = "";

  for (const optionKey of FORMAT_OPTION_KEY_ORDER) {
    const optionValue = options[optionKey];
    if (optionValue !== undefined)
      key += `|${optionKey}:${String(optionValue)}`;
  }

  return key;
}

function getFormatter(locale: string, options?: Intl.DateTimeFormatOptions) {
  if (!options) return getDefaultFormatter(locale);

  let perLocaleIdentityCache = formatterByOptionsIdentityCache.get(locale);
  if (!perLocaleIdentityCache) {
    perLocaleIdentityCache = new WeakMap();
    formatterByOptionsIdentityCache.set(locale, perLocaleIdentityCache);
  } else {
    const formatterByIdentity = perLocaleIdentityCache.get(options);
    if (formatterByIdentity) return formatterByIdentity;
  }

  const optionsKey = getOptionsKey(options);
  if (!optionsKey) {
    const defaultFormatter = getDefaultFormatter(locale);
    perLocaleIdentityCache.set(options, defaultFormatter);
    return defaultFormatter;
  }

  const cacheKey = `${locale}${optionsKey}`;
  const cachedFormatter = formatterCache.get(cacheKey);
  if (cachedFormatter) {
    perLocaleIdentityCache.set(options, cachedFormatter);
    return cachedFormatter;
  }

  const formatter = new Intl.DateTimeFormat(locale, {
    ...DEFAULT_FORMATTER_OPTIONS,
    ...options,
  });

  formatterCache.set(cacheKey, formatter);
  perLocaleIdentityCache.set(options, formatter);

  return formatter;
}

export function createIsoDateFormatter(
  resolvedLocale: string,
  options?: Intl.DateTimeFormatOptions,
) {
  const market = findMarketByLocale(resolvedLocale);
  const formatter = getFormatter(market.locale, options);

  return (isoDate: string) => formatter.format(Date.parse(isoDate));
}

export function formatIsoDate(
  isoDate: string,
  resolvedLocale: string,
  options?: Intl.DateTimeFormatOptions,
) {
  const market = findMarketByLocale(resolvedLocale);
  const formatter = getFormatter(market.locale, options);
  return formatter.format(Date.parse(isoDate));
}
